import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '../lib/api'
import { VehicleAvatar } from '../components/VehicleCard'
import MaintenanceTimeline from '../components/MaintenanceTimeline'
import AddMaintenanceModal from '../components/AddMaintenanceModal'
import UpgradeModal from '../components/UpgradeModal'
import EditVehicleModal from '../components/EditVehicleModal'
import OverLimitModal from '../components/OverLimitModal'
import ShareButton from '../components/ShareToggle'

function formatDate(dateStr) {
  if (!dateStr) return ''
  const [year, month, day] = dateStr.split('-')
  return `${parseInt(month)}/${parseInt(day)}/${year}`
}

export default function VehicleDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [vehicle, setVehicle] = useState(null)
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editVisit, setEditVisit] = useState(null)
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const [upgradeReason, setUpgradeReason] = useState('export maintenance logs as PDF')
  const [editVehicleOpen, setEditVehicleOpen] = useState(false)
  const [premium, setPremium] = useState(false)
  const [overLimit, setOverLimit] = useState(false)
  const [overLimitOpen, setOverLimitOpen] = useState(false)
  const photoInputRef = useRef(null)
  const [editingOdometer, setEditingOdometer] = useState(false)
  const [odometerValue, setOdometerValue] = useState('')

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    if (file.size > 10 * 1024 * 1024) {
      alert('Photo must be less than 10MB')
      e.target.value = ''
      return
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      alert('Photo must be a JPEG, PNG, or WebP image')
      e.target.value = ''
      return
    }
    const formData = new FormData()
    formData.append('vehicle[photo]', file)
    await api.patch(`/vehicles/${id}`, formData)
    fetchVehicle()
    e.target.value = ''
  }

  const handleRemovePhoto = async () => {
    await api.patch(`/vehicles/${id}`, { vehicle: { remove_photo: 1 } })
    fetchVehicle()
  }

  const fetchVehicle = () => {
    api.get(`/vehicles/${id}`)
      .then((res) => setVehicle(res.data))
      .catch(() => navigate('/dashboard'))
      .finally(() => setLoading(false))
  }

  useEffect(() => { fetchVehicle() }, [id])

  useEffect(() => {
    api.get('/billing/subscription').then((res) => setPremium(res.data.premium))
    api.get('/dashboard').then((res) => setOverLimit(res.data.over_vehicle_limit))
  }, [])

  const handleSave = async (formData, visitId) => {
    if (visitId) {
      await api.patch(`/vehicles/${id}/maintenance_logs/${visitId}`, formData)
    } else {
      await api.post(`/vehicles/${id}/maintenance_logs`, formData)
    }
    fetchVehicle()
  }

  const handleDelete = async (visitId) => {
    if (!confirm('Delete this maintenance log and all its entries?')) return
    await api.delete(`/vehicles/${id}/maintenance_logs/${visitId}`)
    fetchVehicle()
  }

  const handleDeleteVehicle = async () => {
    if (!confirm('Delete this vehicle and all its maintenance records?')) return
    await api.delete(`/vehicles/${id}`)
    navigate('/dashboard')
  }

  const openUpgrade = (reason) => {
    setUpgradeReason(reason)
    setUpgradeOpen(true)
  }

  const handleExportPdf = async () => {
    if (!premium) {
      openUpgrade('export maintenance logs as PDF')
      return
    }

    try {
      const res = await api.get(`/vehicles/${id}/export_pdf`, { responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const link = document.createElement('a')
      link.href = url
      link.download = `${vehicle.display_name.replace(/\s+/g, '-').toLowerCase()}-maintenance-log.pdf`
      link.click()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.upgrade_required) {
        openUpgrade('export maintenance logs as PDF')
      } else {
        alert('Failed to export PDF')
      }
    }
  }

  const handleOdometerSave = async () => {
    const val = parseInt(odometerValue)
    if (!isNaN(val) && val >= 0) {
      await api.patch(`/vehicles/${id}`, { vehicle: { current_meter: val } })
      fetchVehicle()
    }
    setEditingOdometer(false)
  }

  const handleDismissOverdue = async (entryId) => {
    await api.patch(`/service_entries/${entryId}/dismiss_overdue`)
    fetchVehicle()
  }

  const handleEdit = (visit) => {
    if (overLimit) { setOverLimitOpen(true); return }
    setEditVisit(visit)
    setModalOpen(true)
  }

  const handleAddNew = () => {
    if (overLimit) { setOverLimitOpen(true); return }
    setEditVisit(null)
    setModalOpen(true)
  }

  if (loading) {
    return <div className="text-center text-gray-500 py-12">Loading...</div>
  }

  if (!vehicle) return null

  const unit = vehicle.meter_label || 'mi'

  return (
    <div>
      <button
        onClick={() => navigate('/dashboard')}
        className="text-sm font-semibold text-muted hover:text-ink mb-4 inline-block py-1"
      >
        &larr; Garage
      </button>

      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handlePhotoUpload}
      />

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5">
        <div className="flex items-center gap-4 min-w-0">
          <button
            type="button"
            onClick={() => photoInputRef.current?.click()}
            className="flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
            title="Click to upload photo"
          >
            <VehicleAvatar vehicle={vehicle} size={72} />
          </button>
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-4xl font-extrabold text-ink leading-tight">{vehicle.display_name}</h1>
            {vehicle.vin && (
              <p className="text-xs sm:text-sm text-muted font-mono mt-1 break-all">{vehicle.vin}</p>
            )}
            {vehicle.photo_url && (
              <button
                onClick={handleRemovePhoto}
                className="text-xs font-semibold text-muted hover:text-accent mt-1"
              >
                Remove photo
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <ShareButton
            vehicleId={vehicle.id}
            shareEnabled={vehicle.share_enabled}
            shareToken={vehicle.share_token}
            premium={premium}
            onUpgrade={() => openUpgrade('share your full maintenance history with photos and receipts')}
            onToggle={(shareData) => setVehicle((v) => ({ ...v, share_enabled: shareData.share_enabled, share_token: shareData.share_token }))}
          />
          <button
            onClick={handleExportPdf}
            className="px-3 py-2 text-sm font-semibold text-ink border-[1.5px] border-ink hover:bg-white"
          >
            Export PDF{!premium && <span className="ml-1.5 text-[10px] font-extrabold tracking-widest text-accent">PREMIUM</span>}
          </button>
          <button
            onClick={() => overLimit ? setOverLimitOpen(true) : setEditVehicleOpen(true)}
            className="px-3 py-2 text-sm font-semibold text-muted hover:text-ink"
          >
            Edit
          </button>
          <button
            onClick={handleDeleteVehicle}
            className="px-3 py-2 text-sm font-semibold text-muted hover:text-accent"
          >
            Delete
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-line border-y-[1.5px] border-ink mt-6">
        <div className="bg-paper px-4 py-3 col-span-2 sm:col-span-1">
          <p className="text-[11px] font-bold uppercase tracking-widest text-muted">{unit === 'hrs' ? 'Current hours' : 'Odometer'}</p>
          {editingOdometer ? (
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <input
                type="number"
                value={odometerValue}
                onChange={(e) => setOdometerValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleOdometerSave()
                  if (e.key === 'Escape') setEditingOdometer(false)
                }}
                autoFocus
                className="w-28 border border-ink bg-white px-2 py-1 font-mono focus:outline-2 focus:outline-accent"
              />
              <span className="text-sm text-muted">{unit}</span>
              <button onClick={handleOdometerSave} className="text-sm font-bold text-accent">Save</button>
              <button onClick={() => setEditingOdometer(false)} className="text-sm text-muted">Cancel</button>
            </div>
          ) : (
            <button
              type="button"
              className="mt-1 flex items-baseline gap-2 text-left"
              onClick={() => { setOdometerValue(vehicle.current_meter || ''); setEditingOdometer(true) }}
              title="Update"
            >
              <span className="font-mono text-xl font-medium text-ink">
                {vehicle.current_meter ? `${vehicle.current_meter.toLocaleString()} ${unit}` : `- ${unit}`}
              </span>
              <span className="text-xs font-semibold text-accent">Update</span>
            </button>
          )}
        </div>
        {[
          [unit === 'hrs' ? 'Hours logged' : 'Driven since purchase', vehicle.distance_driven != null ? `${vehicle.distance_driven.toLocaleString()} ${unit}` : '-'],
          ['Total spent', `$${vehicle.total_maintenance_cost?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || '0.00'}`],
          [`Cost per ${unit}`, vehicle.cost_per_unit != null ? `$${vehicle.cost_per_unit}` : '-'],
          ['Engine', vehicle.engine_model || '-'],
          ['Body', vehicle.body_class || '-'],
          ['Purchased', vehicle.purchase_date ? formatDate(vehicle.purchase_date) : '-'],
        ].map(([label, value]) => (
          <div key={label} className="bg-paper px-4 py-3 min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-widest text-muted">{label}</p>
            <p className="mt-1 font-mono text-base sm:text-lg font-medium text-ink break-words">{value}</p>
          </div>
        ))}
        <div className="bg-paper hidden sm:block" />
      </div>

      {vehicle.upcoming_services?.filter((e) => e.overdue).length > 0 && (
        <div className="mt-6 border-[1.5px] border-accent bg-surface">
          <div className="px-4 pt-3 pb-1 text-[11px] font-extrabold tracking-widest text-accent">OVERDUE</div>
          <div className="divide-y divide-line">
            {vehicle.upcoming_services.filter((e) => e.overdue).map((entry) => (
              <div key={entry.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4 px-4 py-2.5">
                <span className="font-bold text-ink">{entry.title}</span>
                <div className="flex items-center justify-between sm:justify-end gap-4">
                  <span className="font-mono text-xs text-muted">
                    {entry.next_service_date && `Due ${new Date(entry.next_service_date + 'T00:00:00').toLocaleDateString()}`}
                    {entry.next_service_date && entry.next_service_meter && ' / '}
                    {entry.next_service_meter && `${entry.next_service_meter.toLocaleString()} ${unit}`}
                  </span>
                  <button
                    onClick={() => handleDismissOverdue(entry.id)}
                    className="text-sm font-semibold text-muted hover:text-ink py-1"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 mt-10 pb-3 border-b-[3px] border-ink">
        <h2 className="text-xl sm:text-2xl font-extrabold text-ink">
          Maintenance history
          <span className="ml-3 font-mono text-sm font-normal text-muted">{vehicle.maintenance_logs?.length || 0}</span>
        </h2>
        <button
          onClick={handleAddNew}
          className="px-3 sm:px-4 py-2.5 text-sm font-bold text-white bg-accent hover:bg-accent-dark whitespace-nowrap"
        >
          + Add<span className="hidden sm:inline">&nbsp;maintenance</span>
        </button>
      </div>

      <MaintenanceTimeline
        visits={vehicle.maintenance_logs}
        meterLabel={unit}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onDismissOverdue={handleDismissOverdue}
      />

      <AddMaintenanceModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditVisit(null) }}
        onSave={handleSave}
        editVisit={editVisit}
        meterLabel={unit}
        premium={premium}
        onUpgrade={() => openUpgrade('attach photos and receipts to maintenance logs')}
      />

      <UpgradeModal
        isOpen={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        reason={upgradeReason}
      />

      <EditVehicleModal
        isOpen={editVehicleOpen}
        onClose={() => setEditVehicleOpen(false)}
        vehicle={vehicle}
        onSaved={fetchVehicle}
      />

      <OverLimitModal
        isOpen={overLimitOpen}
        onClose={() => setOverLimitOpen(false)}
      />
    </div>
  )
}
